import * as T from "three";
import { moveWithCollisions, type Obstacle } from "./world-data.ts";
import type { ModelAsset } from "./assets.ts";

// An arcade car you can drive: accelerate, brake/reverse, steer (more at speed),
// bump off buildings, trees, lamp posts and other cars. No physics engine.
export const CAR = {
  maxSpeed: 16, // m/s (~36 mph)
  reverse: 5,
  accel: 8,
  brake: 18,
  drag: 0.5,
  turn: 1.7, // rad/s at full lock and speed
  radius: 1.5, // collision radius around the car's centre
};

export interface DriveInput {
  throttle: number; // -1..1 (S/brake negative)
  steer: number; // -1..1 (left positive)
}

export function drivable(scene: T.Scene) {
  let mesh: T.Mesh | null = null;
  const state = { active: false, index: -1, x: 0, z: 0, heading: 0, speed: 0, turnFix: 0 };

  function enter(index: number, x: number, z: number, heading: number, look: { asset: ModelAsset; scale: number; turn: number }) {
    mesh = new T.Mesh(look.asset.geometry, look.asset.materials);
    mesh.scale.setScalar(look.scale);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    scene.add(mesh);
    Object.assign(state, { active: true, index, x, z, heading, speed: 0, turnFix: look.turn });
    place();
  }
  function place() {
    if (!mesh) return;
    mesh.position.set(state.x, -0.15, state.z);
    mesh.rotation.y = state.heading + state.turnFix;
  }
  /** Leave the car: returns where it stopped (to park it there). */
  function exit() {
    if (mesh) scene.remove(mesh);
    mesh = null;
    state.active = false;
    return { index: state.index, x: state.x, z: state.z, heading: state.heading };
  }
  function update(dt: number, input: DriveInput, obstacles: Obstacle[]) {
    if (!state.active) return;
    // Speed: throttle forward; S brakes, then reverses once nearly stopped.
    if (input.throttle > 0.05) state.speed = Math.min(CAR.maxSpeed, state.speed + CAR.accel * input.throttle * dt);
    else if (input.throttle < -0.05) {
      if (state.speed > 0.5) state.speed = Math.max(0, state.speed - CAR.brake * -input.throttle * dt);
      else state.speed = Math.max(-CAR.reverse, state.speed - CAR.accel * 0.6 * -input.throttle * dt);
    }
    state.speed *= 1 - CAR.drag * dt * (Math.abs(input.throttle) < 0.05 ? 1.6 : 0.3);
    // Steering works with speed (and reverses when reversing).
    const grip = Math.max(-1, Math.min(1, state.speed / 6));
    state.heading += input.steer * CAR.turn * grip * dt;
    // Move in short steps, colliding like the player but with a car-sized radius.
    const dx = Math.sin(state.heading) * state.speed * dt, dz = Math.cos(state.heading) * state.speed * dt;
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / 0.25));
    let p = { x: state.x, z: state.z };
    for (let i = 0; i < steps; i++) p = moveWithCollisions(p, dx / steps, dz / steps, obstacles, CAR.radius);
    const intended = Math.hypot(dx, dz), actual = Math.hypot(p.x - state.x, p.z - state.z);
    if (intended > 0.001 && actual < intended * 0.6) state.speed *= 0.35; // bumped into something
    state.x = p.x;
    state.z = p.z;
    place();
  }
  return { state, enter, exit, update };
}
